import * as React from "react";
import { Button } from "~/components/button";
import { Dialog } from "~/components/dialog";
import { Label } from "~/components/form/label";
import { RadioGroup } from "~/components/form/radio-group";
import { TextArea } from "~/components/form/text-area";
import { useToasts } from "~/components/toasts/toasts";
import Feedback1Selected from "~/images/feedback/1-selected.svg?react";
import Feedback1 from "~/images/feedback/1.svg?react";
import Feedback2Selected from "~/images/feedback/2-selected.svg?react";
import Feedback2 from "~/images/feedback/2.svg?react";
import Feedback3Selected from "~/images/feedback/3-selected.svg?react";
import Feedback3 from "~/images/feedback/3.svg?react";
import Feedback4Selected from "~/images/feedback/4-selected.svg?react";
import Feedback4 from "~/images/feedback/4.svg?react";
import Feedback5Selected from "~/images/feedback/5-selected.svg?react";
import Feedback5 from "~/images/feedback/5.svg?react";
import { useFeedback } from "~/mutations/use-feedback";

export const useFeedbackDialog = () => {
  const [showFeedbackDialog, setShowFeedbackDialog] =
    React.useState<boolean>(false);

  const FeedbackDialog = React.useCallback(() => {
    const { addToast } = useToasts();

    const [rating, setRating] = React.useState("");
    const [content, setContent] = React.useState("");
    const options = [
      {
        value: "1",
        icon: Feedback1,
        iconSelected: Feedback1Selected,
        title: "Very Negative",
      },
      {
        value: "2",
        icon: Feedback2,
        iconSelected: Feedback2Selected,
        title: "Negative",
      },
      {
        value: "3",
        icon: Feedback3,
        iconSelected: Feedback3Selected,
        title: "Neutral",
      },
      {
        value: "4",
        icon: Feedback4,
        iconSelected: Feedback4Selected,
        title: "Positive",
      },
      {
        value: "5",
        icon: Feedback5,
        iconSelected: Feedback5Selected,
        title: "Very Positive",
      },
    ];

    const { mutate, status } = useFeedback({
      rating: rating,
      content: content,
      onSuccess: () => {
        addToast({
          variant: "success",
          content: "Feedback sent!",
        });
        setTimeout(() => setShowFeedbackDialog(false), 0);
      },
      onError: () => {
        addToast({
          variant: "error",
          content: "Feedback error!",
        });
      },
    });

    const sendFeedback = (e: any) => {
      e.preventDefault();
      mutate();
    };

    // const parts = window.location.pathname.split("/");
    // const section = parts[parts.length - 1];

    return (
      <Dialog
        modal={false}
        showDialog={showFeedbackDialog}
        setShowDialog={setShowFeedbackDialog}
      >
        <Dialog.Header icon="message-question-square">
          <Dialog.Title>Send feedback</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content>
          <form onSubmit={sendFeedback} id="feedbackForm">
            <div className="space-y-6">
              <p className="text-sm text-balance">
                Lorem ipsum dolor sit amet consectetur adipisicing elit.
                Delectus corporis, possimus expedita iste quibusdam eveniet,
                ipsam quasi esse consequatur corrupti totam aliquam aut fugit
                quae soluta perspiciatis praesentium explicabo ducimus.
              </p>
              <div className="space-y-2">
                {/* <Label htmlFor="rating">Rate your experience</Label> */}
                <RadioGroup
                  name="rating"
                  value={rating}
                  onValueChange={(value) => {
                    setRating(value);
                  }}
                  className="flex items-center justify-start gap-x-4"
                >
                  {options.map(
                    ({ icon: Icon, iconSelected: IconSelected, ...option }) => (
                      <>
                        <RadioGroup.Item
                          value={option.value}
                          id={option.value}
                          className="hidden"
                        />
                        <Label
                          htmlFor={option.value}
                          className="flex h-full cursor-pointer select-none items-center"
                          title={option.title}
                        >
                          {rating === option.value ? (
                            <IconSelected className="w-10 h-10" />
                          ) : (
                            <Icon className="w-10 h-10" />
                          )}
                        </Label>
                      </>
                    ),
                  )}
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label htmlFor="comments">Comments</Label>
                <TextArea
                  id="comments"
                  className="!resize-none"
                  placeholder="I chose the above option because..."
                  onChange={(e) => setContent(e.target.value)}
                ></TextArea>
              </div>
            </div>
          </form>
        </Dialog.Content>
        <Dialog.Footer>
          <Button
            variant="outline"
            onClick={() => setShowFeedbackDialog(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={status === "pending"}
            type="submit"
            form="feedbackForm"
          >
            Send feedback
          </Button>
        </Dialog.Footer>
      </Dialog>
    );
  }, [showFeedbackDialog, setShowFeedbackDialog]);

  return React.useMemo(
    () => ({ setShowFeedbackDialog, FeedbackDialog }),
    [setShowFeedbackDialog, FeedbackDialog],
  );
};
